import { Form, Link, useActionData, useNavigation } from "react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import type { AccountDeletion, Me } from "@repo/shared-types";
import { requireSessionWith } from "@/lib/guards";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { tenantPath } from "@/lib/tenants";
import { SpacePicture } from "@/components/avatar";
import { ErrorNote } from "@/components/form";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldDescription, FieldLegend, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AccountPage, type ActionResult } from "@/routes/account-section";
import { useSession } from "@/routes/root";

/**
 * Closing the account, the one page under Account that explains itself
 * (ADR 0068). The consequences are shown before anything is pressed, not
 * after. What blocks deletion is a list of spaces with other people in them,
 * and each one links to where it can be handed on or wound up.
 */

export async function deleteAccountLoader({ request }: { request: Request }) {
  await requireSessionWith(request, () => read(queries.accountDeletion()));
  return null;
}

export default function DeleteAccount() {
  const session = useSession();
  const { data: deletion } = useSuspenseQuery(queries.accountDeletion());
  // The loader let the page draw only with a session; deleting it leaves none.
  return session && <Deletion session={session} deletion={deletion} />;
}

function Deletion({ session, deletion }: { session: Me; deletion: AccountDeletion }) {
  const result = useActionData() as ActionResult | undefined;
  const busy = useNavigation().state === "submitting";
  const { blockers } = deletion;
  const username = session.user.username;

  return (
    <AccountPage title="Delete account">
      <div className="space-y-2 text-muted-foreground">
        <p>
          This removes your account, your personal space, any space you own that nobody else is in, and everything
          you have written. It cannot be undone.
        </p>
        <p>Spaces owned by someone else simply lose you as a member.</p>
      </div>

      <Form method="post" className="space-y-5">
        <input type="hidden" name="intent" value="delete" />
        {blockers.length > 0 && (
          <FieldSet className="gap-3">
            <FieldLegend variant="label">Spaces you own with other people in them</FieldLegend>
            <FieldDescription>
              Tick each one to wind it up along with your account, or hand it to another member instead. Every one has
              to be accounted for — there is no "delete everything" shortcut, because each of these is somebody else's
              board too.
            </FieldDescription>

            <ul className="divide-y border-y">
              {blockers.map(({ tenant, memberCount }) => (
                // On a phone, the way to hand it on goes under its name.
                <li key={tenant.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3">
                  {/* shadcn's checkbox, inside the form, sends the space's
                      id as a box would: one field per space ticked. */}
                  <Checkbox id={`delete-${tenant.id}`} name="deleteSpaces" value={tenant.id} />
                  <Label htmlFor={`delete-${tenant.id}`} className="min-w-48 flex-1 gap-3 leading-normal font-normal">
                    <SpacePicture space={tenant} className="shrink-0" />
                    <span className="min-w-0">
                      <span className="block font-medium wrap-anywhere">Delete {tenant.name}</span>
                      <span className="block text-muted-foreground">{memberCount} members</span>
                    </span>
                  </Label>
                  {/* Handing it on is a role change, on its members page. */}
                  <Button asChild variant="outline" size="sm" className="ml-7 sm:ml-0">
                    <Link to={`${tenantPath(tenant)}/members`}>Hand it on</Link>
                  </Button>
                </li>
              ))}
            </ul>
          </FieldSet>
        )}

        <Field>
          <div className="flex flex-wrap gap-2">
            <Input
              name="confirm"
              autoComplete="off"
              placeholder={username}
              aria-label={`Type ${username} to confirm`}
              className="h-10 min-w-40 flex-1"
            />
            <Button type="submit" variant="destructive" size="lg" disabled={busy}>
              Delete my account
            </Button>
          </div>
          <FieldDescription>
            Type <strong className="font-medium text-foreground">{username}</strong> to confirm.
          </FieldDescription>
        </Field>
        {result?.intent === "delete" && result.error && <ErrorNote message={result.error} />}
      </Form>
    </AccountPage>
  );
}
